/**
 * MerchantProductPage.jsx
 * ESDRAS MERCHANT MARKETPLACE — Individual Product Page (Public)
 * Route: /marketplace/product/:productId
 * Features: Image carousel, product details, qty selector, reviews, share, buy now
 * Auth: NOT required to VIEW — required to checkout (handled in Merchant_checkout.jsx)
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, collection, query, where, orderBy, limit, onSnapshot,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../firebase';

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const GOLD       = '#B8860B';
const DEEP_BLACK = '#050a0f';
const CARD_BG    = '#0a0f14';
const BORDER     = '#1a2631';
const SAFE_IMG   = 'https://placehold.co/600x600/050a0f/B8860B?text=Product+Image';
const DELIVERY_FEE_LAGOS     = 2500;
const DELIVERY_FEE_OUTSIDE   = 10000;
const SUPER_ADMINS = ['FFXDokpj5IXR3SFSvJyDu70LRp73', 'GbjKMdeVEmTgMefcU1cEHsEOstn1'];

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function MerchantProductPage() {
  const { productId } = useParams();
  const navigate      = useNavigate();

  const [product,       setProduct]       = useState(null);
  const [merchant,      setMerchant]      = useState(null);
  const [reviews,       setReviews]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [notFound,      setNotFound]      = useState(false);
  const [activeImg,     setActiveImg]     = useState(0);
  const [qty,           setQty]           = useState(1);
  const [user,          setUser]          = useState(null);
  const [copied,        setCopied]        = useState(false);

  // ── Auth observer ───────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setUser(u));
    return () => unsub();
  }, []);

  // ── Load product ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!productId) return;
    const load = async () => {
      const snap = await getDoc(doc(db, 'merchant_products', productId));
      if (!snap.exists() || snap.data().status !== 'approved') {
        setNotFound(true); setLoading(false); return;
      }
      const pData = { id: snap.id, ...snap.data() };
      setProduct(pData);

      // Load merchant info
      const mSnap = await getDoc(doc(db, 'merchants', pData.merchantId)).catch(() => null);
      if (mSnap?.exists()) setMerchant({ id: mSnap.id, ...mSnap.data() });

      setLoading(false);
    };
    load();
  }, [productId]);

  // ── Load reviews ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!productId) return;
    const q = query(
      collection(db, 'merchant_reviews'),
      where('productId', '==', productId),
      orderBy('createdAt', 'desc'),
      limit(20),
    );
    const unsub = onSnapshot(q, snap => {
      setReviews(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => {});
    return () => unsub();
  }, [productId]);

  // ── Share ────────────────────────────────────────────────────────────────
  const handleShare = useCallback((method) => {
    const url  = `${window.location.origin}/marketplace/product/${productId}`;
    const text = `Check out ${product?.name} on Esdras Marketplace: ${url}`;
    if (method === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    } else {
      navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    }
  }, [productId, product]);

  // ── Add to Cart / Buy Now ─────────────────────────────────────────────────
  const handleBuyNow = () => {
    navigate('/merchant/checkout', {
      state: { productId, product, qty, merchantId: product.merchantId },
    });
  };

  // ── OG meta tags for social sharing ──────────────────────────────────────
  useEffect(() => {
    if (!product) return;
    document.title = `${product.name} — Esdras Marketplace`;
  }, [product]);

  // ────────────────────────────────────────────────────────────────────────
  // LOADING
  if (loading) return (
    <div style={styles.page}>
      <div style={styles.loadingBox}>
        <div style={styles.spinner} />
        <p style={{ color: GOLD, letterSpacing: '3px', fontSize: '0.75rem', marginTop: '16px' }}>LOADING PRODUCT...</p>
      </div>
    </div>
  );

  // NOT FOUND
  if (notFound) return (
    <div style={styles.page}>
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '3rem', marginBottom: '15px' }}>🔍</div>
        <h2 style={{ color: GOLD }}>PRODUCT NOT FOUND</h2>
        <p style={{ color: '#555', marginBottom: '25px' }}>This product may have been removed or is no longer available.</p>
        <button onClick={() => navigate('/')} style={styles.goldBtn}>← BACK TO ESDRAS</button>
      </div>
    </div>
  );

  const images = (product.imageUrls || []).filter(Boolean);
  if (images.length === 0) images.push(SAFE_IMG);
  const isOutOfStock = (product.qty || 0) === 0;
  const maxQty       = Math.min(product.qty || 1, 10);
  const catInfo      = product.category === 'hair_care'
    ? { label: 'HAIR CARE', icon: '💆', color: GOLD }
    : { label: 'SKIN CARE', icon: '✨', color: '#a8dadc' };

  // ────────────────────────────────────────────────────────────────────────
  // RENDER
  return (
    <div style={styles.page}>
      {/* ── Breadcrumb / Nav ── */}
      <nav style={styles.breadcrumb}>
        <button onClick={() => navigate('/')} style={styles.navLink}>Esdras</button>
        <span style={{ color: '#333' }}> / </span>
        <button onClick={() => navigate('/marketplace')} style={styles.navLink}>Marketplace</button>
        <span style={{ color: '#333' }}> / </span>
        <span style={{ color: GOLD, fontSize: '0.78rem' }}>{product.name}</span>
      </nav>

      {/* ── Main Product Layout ── */}
      <div style={styles.productGrid}>

        {/* ── LEFT: Image Carousel ── */}
        <div style={styles.imageSection}>
          {/* Main Image */}
          <div style={styles.mainImgWrapper}>
            <img
              src={images[activeImg] || SAFE_IMG}
              alt={product.name}
              style={styles.mainImg}
              onError={e => { e.target.src = SAFE_IMG; }}
            />
            {isOutOfStock && (
              <div style={styles.outOfStockBanner}>OUT OF STOCK</div>
            )}
            {/* Nav arrows if multiple images */}
            {images.length > 1 && (
              <>
                <button style={styles.imgArrow} onClick={() => setActiveImg(i => (i - 1 + images.length) % images.length)}>‹</button>
                <button style={{ ...styles.imgArrow, left: 'auto', right: '12px' }}
                  onClick={() => setActiveImg(i => (i + 1) % images.length)}>›</button>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div style={styles.thumbRow}>
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)}
                  style={{ ...styles.thumb, borderColor: i === activeImg ? GOLD : BORDER }}>
                  <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.src = SAFE_IMG; }} />
                </button>
              ))}
            </div>
          )}
          {images.length > 1 && (
            <div style={styles.dotRow}>
              {images.map((_, i) => (
                <button key={i} onClick={() => setActiveImg(i)}
                  style={{ ...styles.dot, background: i === activeImg ? GOLD : '#1a2631' }} />
              ))}
            </div>
          )}
        </div>

        {/* ── RIGHT: Product Info ── */}
        <div style={styles.infoSection}>
          {/* Category Badge */}
          <div style={{ ...styles.catBadge, color: catInfo.color, borderColor: catInfo.color + '44' }}>
            {catInfo.icon} {catInfo.label}
          </div>

          {/* Product Name */}
          <h1 style={styles.productTitle}>{product.name}</h1>

          {/* Merchant Name */}
          <div style={styles.merchantRow}>
            {merchant?.logoUrl && (
              <img src={merchant.logoUrl} alt="" style={styles.merchantAvatar}
                onError={e => { e.target.style.display = 'none'; }} />
            )}
            <div>
              <p style={styles.soldBy}>Sold by</p>
              <button onClick={() => navigate(`/merchant/${product.merchantId}`)} style={styles.merchantName}>
                {product.merchantName}
              </button>
            </div>
          </div>

          {/* Rating */}
          {product.averageRating > 0 && (
            <div style={styles.ratingRow}>
              {Array.from({ length: 5 }).map((_, i) => (
                <span key={i} style={{ color: i < Math.round(product.averageRating) ? GOLD : '#2a2a2a', fontSize: '1rem' }}>★</span>
              ))}
              <span style={{ color: '#666', fontSize: '0.8rem', marginLeft: '8px' }}>
                {product.averageRating.toFixed(1)} ({product.reviewCount} review{product.reviewCount !== 1 ? 's' : ''})
              </span>
            </div>
          )}

          {/* Price */}
          <div style={styles.priceBlock}>
            <span style={styles.displayPrice}>₦{Number(product.displayPrice || 0).toLocaleString()}</span>
            <span style={styles.baseNote}>
              Base ₦{Number(product.basePrice || 0).toLocaleString()} + platform fee
            </span>
          </div>

          {/* Delivery Info */}
          <div style={styles.deliveryBox}>
            <div style={styles.deliveryRow}>
              <span style={{ fontSize: '1rem' }}>🚚</span>
              <div>
                <p style={{ margin: '0 0 2px', color: '#ccc', fontSize: '0.82rem', fontWeight: '600' }}>
                  Delivery: <span style={{ color: GOLD }}>₦{DELIVERY_FEE_LAGOS.toLocaleString()}</span> Lagos
                  {' / '}
                  <span style={{ color: '#a8dadc' }}>₦{DELIVERY_FEE_OUTSIDE.toLocaleString()}</span> Outside Lagos
                </p>
                <p style={{ margin: 0, color: '#444', fontSize: '0.72rem' }}>Per order, flat rate. Lagos only at this time.</p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div style={styles.descBlock}>
            <p style={styles.descLabel}>DESCRIPTION</p>
            <p style={styles.descText}>{product.description}</p>
          </div>

          {/* NAFDAC */}
          <div style={styles.nafdacRow}>
            <span style={styles.nafdacLabel}>NAFDAC REG:</span>
            <span style={styles.nafdacNum}>{product.nafdacNumber || 'N/A'}</span>
          </div>

          {/* Stock */}
          <p style={{ color: isOutOfStock ? '#ff4d4d' : '#4caf50', fontSize: '0.8rem', fontWeight: '700', margin: '10px 0' }}>
            {isOutOfStock ? '✗ OUT OF STOCK' : `✓ IN STOCK (${product.qty} available)`}
          </p>

          {/* Qty Selector */}
          {!isOutOfStock && (
            <div style={styles.qtyRow}>
              <span style={styles.qtyLabel}>QTY</span>
              <button style={styles.qtyBtn} onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
              <span style={styles.qtyNum}>{qty}</span>
              <button style={styles.qtyBtn} onClick={() => setQty(q => Math.min(maxQty, q + 1))}>+</button>
            </div>
          )}

          {/* CTA Buttons */}
          <div style={styles.ctaRow}>
            <button
              onClick={handleBuyNow}
              disabled={isOutOfStock}
              style={{ ...styles.buyBtn, opacity: isOutOfStock ? 0.4 : 1, cursor: isOutOfStock ? 'not-allowed' : 'pointer' }}>
              {isOutOfStock ? 'OUT OF STOCK' : '🛒 BUY NOW'}
            </button>
          </div>

          {/* Share Buttons */}
          <div style={styles.shareRow}>
            <span style={styles.shareLabel}>SHARE:</span>
            <button onClick={() => handleShare('whatsapp')} style={styles.shareBtn}>
              📲 WhatsApp
            </button>
            <button onClick={() => handleShare('copy')} style={styles.shareBtn}>
              {copied ? '✅ Copied!' : '🔗 Copy Link'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Reviews Section ── */}
      <div style={styles.reviewsSection}>
        <h2 style={styles.reviewsTitle}>CUSTOMER REVIEWS</h2>

        {reviews.length === 0 ? (
          <div style={styles.noReviews}>
            <p style={{ color: '#555', fontSize: '0.88rem' }}>No reviews yet. Be the first to review after your purchase.</p>
          </div>
        ) : (
          <div style={styles.reviewsGrid}>
            {reviews.map(r => (
              <div key={r.id} style={styles.reviewCard}>
                <div style={styles.reviewHeader}>
                  <div style={styles.reviewAvatar}>{(r.reviewerName || 'A')[0].toUpperCase()}</div>
                  <div>
                    <p style={styles.reviewerName}>{r.reviewerName || 'Verified Buyer'}</p>
                    <div style={{ display: 'flex' }}>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <span key={i} style={{ color: i < r.rating ? GOLD : '#2a2a2a', fontSize: '0.8rem' }}>★</span>
                      ))}
                    </div>
                  </div>
                  <p style={styles.reviewDate}>
                    {r.createdAt?.toDate ? r.createdAt.toDate().toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                  </p>
                </div>
                <p style={styles.reviewComment}>{r.comment}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Footer Spacer ── */}
      <div style={{ height: '60px' }} />

      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
const styles = {
  page: {
    minHeight: '100vh', background: DEEP_BLACK, color: 'white',
    fontFamily: "'Montserrat', 'Inter', sans-serif",
  },
  loadingBox: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minHeight: '100vh',
  },
  spinner: {
    width: '40px', height: '40px', border: `3px solid ${GOLD}22`,
    borderTop: `3px solid ${GOLD}`, borderRadius: '50%', animation: 'spin 1s linear infinite',
  },
  breadcrumb: {
    padding: '18px 40px', background: '#020507',
    borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: '4px',
  },
  navLink: {
    background: 'transparent', border: 'none', color: '#555', cursor: 'pointer',
    fontSize: '0.78rem', padding: '0', fontFamily: 'inherit',
  },
  productGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '50px',
    maxWidth: '1100px', margin: '0 auto', padding: '50px 40px',
    '@media(max-width: 768px)': { gridTemplateColumns: '1fr' },
  },
  imageSection: { display: 'flex', flexDirection: 'column', gap: '12px' },
  mainImgWrapper: {
    width: '100%', aspectRatio: '1/1', background: CARD_BG,
    borderRadius: '20px', overflow: 'hidden', position: 'relative',
    border: `1px solid ${BORDER}`,
  },
  mainImg: { width: '100%', height: '100%', objectFit: 'cover' },
  outOfStockBanner: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: '#ff4d4d', fontWeight: '900', fontSize: '1.5rem',
    letterSpacing: '3px',
  },
  imgArrow: {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: '12px',
    background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white',
    width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer',
    fontSize: '1.4rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
    backdropFilter: 'blur(8px)',
  },
  thumbRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  thumb: {
    width: '60px', height: '60px', borderRadius: '10px', overflow: 'hidden',
    border: '2px solid', cursor: 'pointer', background: CARD_BG, padding: 0,
    flexShrink: 0,
  },
  dotRow: { display: 'flex', gap: '6px', justifyContent: 'center' },
  dot: { width: '8px', height: '8px', borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0 },
  infoSection: { display: 'flex', flexDirection: 'column', gap: '14px' },
  catBadge: {
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    fontSize: '0.62rem', fontWeight: '900', letterSpacing: '1px',
    border: '1px solid', padding: '4px 12px', borderRadius: '20px',
    alignSelf: 'flex-start',
  },
  productTitle: { color: 'white', fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: '900', margin: 0, lineHeight: '1.2' },
  merchantRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  merchantAvatar: { width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: `1px solid ${BORDER}` },
  soldBy: { color: '#444', fontSize: '0.7rem', margin: '0 0 2px' },
  merchantName: {
    background: 'transparent', border: 'none', color: GOLD, cursor: 'pointer',
    fontWeight: '700', fontSize: '0.88rem', padding: 0, fontFamily: 'inherit',
  },
  ratingRow: { display: 'flex', alignItems: 'center' },
  priceBlock: { display: 'flex', flexDirection: 'column', gap: '3px' },
  displayPrice: { color: GOLD, fontSize: '2rem', fontWeight: '900' },
  baseNote: { color: '#444', fontSize: '0.72rem' },
  deliveryBox: { background: CARD_BG, borderRadius: '12px', padding: '14px', border: `1px solid ${BORDER}` },
  deliveryRow: { display: 'flex', gap: '12px', alignItems: 'flex-start' },
  descBlock: { borderTop: `1px solid ${BORDER}`, paddingTop: '14px' },
  descLabel: { color: GOLD, fontSize: '0.62rem', letterSpacing: '2px', fontWeight: '900', margin: '0 0 8px' },
  descText: { color: '#bbb', fontSize: '0.88rem', lineHeight: '1.7', margin: 0 },
  nafdacRow: { display: 'flex', gap: '10px', alignItems: 'center' },
  nafdacLabel: { color: '#555', fontSize: '0.72rem', fontWeight: '700', letterSpacing: '1px' },
  nafdacNum: { color: '#aaa', fontSize: '0.82rem', fontFamily: 'monospace' },
  qtyRow: {
    display: 'flex', alignItems: 'center', gap: '12px',
    background: CARD_BG, borderRadius: '12px', padding: '10px 16px',
    border: `1px solid ${BORDER}`, alignSelf: 'flex-start',
  },
  qtyLabel: { color: '#555', fontSize: '0.7rem', fontWeight: '700', letterSpacing: '1px' },
  qtyBtn: {
    width: '32px', height: '32px', background: '#1a2631', border: 'none',
    borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '1.2rem',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  qtyNum: { color: 'white', fontWeight: '900', fontSize: '1.1rem', minWidth: '30px', textAlign: 'center' },
  ctaRow: { display: 'flex', gap: '12px' },
  buyBtn: {
    flex: 1, background: GOLD, color: 'black', border: 'none', borderRadius: '14px',
    padding: '18px', fontWeight: '900', fontSize: '0.95rem', letterSpacing: '1px',
    fontFamily: 'inherit', transition: 'all 0.2s',
  },
  shareRow: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  shareLabel: { color: '#555', fontSize: '0.68rem', letterSpacing: '1px', fontWeight: '700' },
  shareBtn: {
    background: CARD_BG, border: `1px solid ${BORDER}`, color: '#aaa',
    padding: '8px 14px', borderRadius: '50px', cursor: 'pointer', fontSize: '0.78rem',
    fontFamily: 'inherit',
  },
  goldBtn: {
    background: GOLD, color: 'black', border: 'none', borderRadius: '50px',
    padding: '14px 30px', fontWeight: '900', cursor: 'pointer', fontSize: '0.88rem',
    fontFamily: 'inherit',
  },
  reviewsSection: { maxWidth: '1100px', margin: '0 auto', padding: '0 40px 40px' },
  reviewsTitle: {
    color: 'white', fontWeight: '900', letterSpacing: '3px', fontSize: '1.1rem',
    margin: '0 0 24px', paddingTop: '40px', borderTop: `1px solid ${BORDER}`,
  },
  noReviews: { textAlign: 'center', padding: '30px', background: CARD_BG, borderRadius: '14px', border: `1px solid ${BORDER}` },
  reviewsGrid: { display: 'flex', flexDirection: 'column', gap: '14px' },
  reviewCard: { background: CARD_BG, borderRadius: '14px', padding: '18px', border: `1px solid ${BORDER}` },
  reviewHeader: { display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' },
  reviewAvatar: {
    width: '36px', height: '36px', borderRadius: '50%',
    background: `${GOLD}22`, border: `1px solid ${GOLD}44`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: GOLD, fontWeight: '900', fontSize: '0.9rem', flexShrink: 0,
  },
  reviewerName: { color: 'white', fontWeight: '700', fontSize: '0.88rem', margin: '0 0 4px' },
  reviewDate: { color: '#444', fontSize: '0.7rem', margin: '0', marginLeft: 'auto', whiteSpace: 'nowrap' },
  reviewComment: { color: '#bbb', fontSize: '0.85rem', lineHeight: '1.6', margin: 0 },
};
